import { HslTheme, ThemeModeSchema } from "./schema";
import {
  getHslThemeCssProperties,
  HslThemeNestedSelectors,
} from "./hsl-theme-css-properties";

/**
 * Generates base CSS properties including page width and border radius
 */
export function getBaseCssProperties(
  hslTheme: HslTheme | null,
  pageWidth?: number
): string {
  if (!hslTheme) return ``;

  return `
      :root {
        ${pageWidth ? `--zi-pageWidth: ${pageWidth}px;` : ""}
        --radius: ${hslTheme.radius};
      }
    `;
}

/**
 * Generates color CSS properties based on theme mode
 */
export function getColorCssProperties(
  hslTheme: HslTheme | null,
  allowSystemThemeMode: boolean = false
): string {
  if (!hslTheme) return ``;

  const { mode } = hslTheme;

  switch (mode) {
    case ThemeModeSchema.enum.Light:
      return getHslThemeCssProperties([":root"], hslTheme, "Light");
    case ThemeModeSchema.enum.Dark:
      return getHslThemeCssProperties([":root"], hslTheme, "Dark");
    case ThemeModeSchema.enum.System: {
      const darkQualifiers: HslThemeNestedSelectors = allowSystemThemeMode
        ? ["@media (prefers-color-scheme: dark)", ":root"]
        : [".dark"];

      return `
          ${getHslThemeCssProperties([":root"], hslTheme, "Light")}
          ${getHslThemeCssProperties(darkQualifiers, hslTheme, "Dark")}
        `;
    }
    default: {
      const _exhaustiveCheck: never = mode;
      return _exhaustiveCheck;
    }
  }
}

/**
 * Generates all CSS properties (base + color) in a single function
 * This is the recommended way to generate theme CSS for most use cases
 */
export function getAllCssProperties(
  hslTheme: HslTheme | null,
  options: {
    pageWidth?: number;
    allowSystemThemeMode?: boolean;
  } = {}
): string {
  const { pageWidth, allowSystemThemeMode = false } = options;

  const baseCss = getBaseCssProperties(hslTheme, pageWidth);
  const colorCss = getColorCssProperties(hslTheme, allowSystemThemeMode);

  return baseCss + colorCss;
}
